import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { arrayMoveMutable } from 'array-move';
import * as _ from 'es-toolkit';
import WindowForm from '../WindowForm/WindowForm';
import Draggable from '../Draggable/Draggable';
import CardIconAction from '../CardIconAction/CardIconAction';

const SortableWindowCard = SortableElement(({
    window,
    idx,
    collapsed,
    onCollapse,
    screenings,
    title,
    headerDetails,
    deleteTitle,
    onRemove,
}) => (
    <Col id={`tickettype-window-${idx}`} xs="6" className="mb-3">
        <Draggable
            id={idx}
            draggable
            onCollapse={onCollapse}
            collapsed={collapsed}
            keepFieldsMounted
            title={title}
            headerDetails={headerDetails}
            cardbody={
                <WindowForm window={window} idx={idx} screenings={screenings} />
            }
            cardActions={
                <CardIconAction
                    id={`delete-window-${idx}`}
                    icon="fa-trash"
                    danger
                    title={deleteTitle}
                    onClick={() => onRemove(idx)}
                />
            }
        />
    </Col>
));

const SortableWindowsGrid = SortableContainer(({
    items,
    collapse,
    onCollapse,
    screenings,
    buildWindowTitle,
    buildWindowHeaderDetails,
    deleteTitle,
    onRemove,
}) => (
    <Row className="sortable-grid mx-0">
        {(items || []).map((window, index) => (
            <SortableWindowCard
                key={`window-${index}`}
                index={index}
                idx={index}
                window={window}
                collapsed={collapse[index]}
                onCollapse={onCollapse}
                screenings={screenings}
                title={buildWindowTitle(index)}
                headerDetails={buildWindowHeaderDetails(window)}
                deleteTitle={deleteTitle}
                onRemove={onRemove}
            />
        ))}
    </Row>
));

const WindowsForm = ({ windows, remove, updateWindowOrder, screenings, focusedWindowIndex }) => {
    const { t } = useTranslation();
    const [collapse, setCollapse] = useState([]);
    const remove_ref = useRef(remove);
    const update_order_ref = useRef(updateWindowOrder);

    remove_ref.current = remove;
    update_order_ref.current = updateWindowOrder;

    const sortedWindows = useMemo(() => windows || [], [windows]);
    const windows_length = sortedWindows.length;

    // Keep open/closed state across saves (same length). Only resize when
    // windows are added/removed — mirror PricingsForm keyed expand behavior.
    useEffect(() => {
        setCollapse(prev => {
            if (prev.length === windows_length)
                return prev;
            const next = Array.from({ length: windows_length }, (_, idx) => prev[idx] ?? false);
            if (windows_length > prev.length)
                next[windows_length - 1] = true;
            return next;
        });
    }, [windows_length]);

    useEffect(() => {
        if (focusedWindowIndex == null)
            return;
        setCollapse(prev => {
            if (focusedWindowIndex < 0 || focusedWindowIndex >= prev.length)
                return prev;
            if (prev[focusedWindowIndex])
                return prev;
            const next = [...prev];
            next[focusedWindowIndex] = true;
            return next;
        });
        const target = document.getElementById(`tickettype-window-${focusedWindowIndex}`);
        if (target) {
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [focusedWindowIndex, windows_length]);

    const onCollapse = useCallback((id) => {
        setCollapse(prev => {
            const next = [...prev];
            next[id] = !prev[id];
            return next;
        });
    }, []);

    const removeWindowAtIndex = useCallback((idx) => {
        setCollapse(prev => {
            const temp = _.cloneDeep(prev);
            temp.splice(idx, 1);
            return temp;
        });
        remove_ref.current(idx);
    }, []);

    const buildWindowTitle = useCallback((idx) => (
        <div>
            <b>{t('tickettypes.window.block_title', { number: idx + 1 })}</b>
        </div>
    ), [t]);

    const buildWindowHeaderDetails = useCallback((window) => {
        const nbookings = window?.nbookings ?? 1;
        return (
            <div className="Draggable-header-detailLine">
                {t('tickettypes.window.collapsed_summary', { count: nbookings })}
            </div>
        );
    }, [t]);

    const onSortEnd = useCallback(({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex)
            return;

        setCollapse(prev => {
            const nextCollapse = [...prev];
            arrayMoveMutable(nextCollapse, oldIndex, newIndex);
            return nextCollapse;
        });

        const nextWindows = [...(windows || [])];
        arrayMoveMutable(nextWindows, oldIndex, newIndex);
        update_order_ref.current(nextWindows);
    }, [windows]);

    if (!sortedWindows.length)
        return null;

    return (
        <div className="WindowsForm">
            <SortableWindowsGrid
                items={sortedWindows}
                collapse={collapse}
                onCollapse={onCollapse}
                screenings={screenings}
                buildWindowTitle={buildWindowTitle}
                buildWindowHeaderDetails={buildWindowHeaderDetails}
                deleteTitle={t('common.card_action_delete_window')}
                onRemove={removeWindowAtIndex}
                axis="xy"
                onSortEnd={onSortEnd}
                useDragHandle
                useWindowAsScrollContainer
                helperClass="sortableHelper"
            />
        </div>
    );
};

export default WindowsForm;
